import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import {
  LayoutDashboard, Users, TrendingUp, CheckSquare,
  MessageSquare, LogOut, ChevronLeft, ChevronRight, Menu, X,
} from "lucide-react";
import { useAuth } from "../Auth-Context/AuthContext";
import { getDashboardData } from "../api/services";

export function dispatchSidebarUpdate(payload = {}) {
  window.dispatchEvent(
    new CustomEvent("crm:sidebar:update", { detail: payload })
  );
}

// Define nav items for Manager
const MANAGER_NAV_ITEMS = [
  { id: "dashboard",    label: "Dashboard",    icon: LayoutDashboard, path: "/dashboard"    },
  { id: "contacts",     label: "Companies",     icon: Users,           path: "/contacts",     badgeKey: "contacts"     },
  { id: "pipeline",     label: "Pipeline",     icon: TrendingUp,      path: "/pipeline"      },
  { id: "tasks",        label: "Tasks",        icon: CheckSquare,     path: "/tasks",        badgeKey: "tasks"        },
  { id: "interactions", label: "Interactions", icon: MessageSquare,   path: "/interactions", badgeKey: "interactions" },
];

// Define nav items for Employee (no Interactions)
const EMPLOYEE_NAV_ITEMS = [
  { id: "dashboard",    label: "Dashboard",    icon: LayoutDashboard, path: "/dashboard"    },
  { id: "contacts",     label: "Companies",     icon: Users,           path: "/contacts",     badgeKey: "contacts"     },
  { id: "pipeline",     label: "Pipeline",     icon: TrendingUp,      path: "/pipeline"      },
  { id: "tasks",        label: "Tasks",        icon: CheckSquare,     path: "/tasks",        badgeKey: "tasks"        },
];

export default function Sidebar() {
  const [collapsed,      setCollapsed]      = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile,       setIsMobile]       = useState(false);
  const [badgeData,      setBadgeData]      = useState({ contacts: 0, tasks: 0, interactions: 0 });

  const navigate    = useNavigate();
  const location    = useLocation();
  const { logout, user } = useAuth();
  const prevPathRef = useRef(location.pathname);

  // Determine which nav items to show based on role
  const isManager = user?.role === "MANAGER";
  const NAV_ITEMS = isManager ? MANAGER_NAV_ITEMS : EMPLOYEE_NAV_ITEMS;

  // ── Responsive ──────────────────────────────────────────────────────────
  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setMobileMenuOpen(false);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // ── Fetch from API ───────────────────────────────────────────────────────
  const fetchBadges = useCallback(async () => {
    try {
      const data = await getDashboardData();
      setBadgeData((prev) => ({
        ...prev,
        contacts:     data.totalContacts     ?? 0,
        interactions: data.totalInteractions ?? 0,
      }));
    } catch (err) {
      console.error("[Sidebar] badge fetch failed:", err);
    }
  }, []);

  // Fetch on mount
  useEffect(() => { fetchBadges(); }, [fetchBadges]);

  // Re-fetch when user navigates to a different page
  useEffect(() => {
    if (location.pathname !== prevPathRef.current) {
      prevPathRef.current = location.pathname;
      fetchBadges();
    }
  }, [location.pathname, fetchBadges]);
  
  useEffect(() => {
    const handler = (e) => {
      const { contacts, tasks, interactions } = e.detail ?? {};
      setBadgeData((prev) => ({
        contacts:     typeof contacts     === "number" ? contacts     : prev.contacts,
        tasks:        typeof tasks        === "number" ? tasks        : prev.tasks,
        interactions: typeof interactions === "number" ? interactions : prev.interactions,
      }));
    };
    window.addEventListener("crm:sidebar:update", handler);
    return () => window.removeEventListener("crm:sidebar:update", handler);
  }, []);

  const handleLogout = () => { logout(); navigate("/login"); };

  // ── Shared sidebar body ──────────────────────────────────────────────────
  const SidebarContent = ({ mobile = false }) => (
    <>
      <div
        className="flex items-center flex-shrink-0 px-4 overflow-hidden border-b border-neutral-100"
        style={{ height: "56px" }}
      >
        <img src="/IZ-Logo.png" alt="IZ Sales" className="flex-shrink-0 object-contain w-auto h-7" />
        {(!collapsed || mobile) && (
          <div className="flex flex-col ml-2 overflow-hidden leading-none">
            <span className="text-[11px] font-bold text-neutral-800 whitespace-nowrap">IZSOFTWARES CRM</span>
          </div>
        )}
      </div>

      <nav className="flex-1 px-2 py-3 flex flex-col gap-0.5 overflow-y-auto">
        {NAV_ITEMS.map((item) => {
          const Icon     = item.icon;
          const isActive = location.pathname === item.path;
          const badge    = item.badgeKey ? badgeData[item.badgeKey] : null;

          return (
            <button
              key={item.id}
              onClick={() => { navigate(item.path); if (mobile) setMobileMenuOpen(false); }}
              className="flex items-center gap-2.5 w-full rounded-lg px-2.5 h-9 transition-all duration-150 text-left"
              style={isActive ? { background: "#009DB9", color: "#fff" } : { color: "#737373" }}
              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = "#f5f5f5"; }}
              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
            >
              <Icon size={16} strokeWidth={isActive ? 2.5 : 2} className="flex-shrink-0" />
              {(!collapsed || mobile) && (
                <span className="text-[13px] font-medium flex-1 truncate">{item.label}</span>
              )}
              {(!collapsed || mobile) && badge > 0 && (
                <span
                  className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                  style={
                    isActive
                      ? { background: "rgba(255,255,255,0.25)", color: "#fff" }
                      : { background: "rgba(0,157,185,0.12)", color: "#009DB9" }
                  }
                >
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      <div className="px-2 py-3 border-t border-neutral-100">
        <button
          onClick={handleLogout}
          className="flex items-center gap-2.5 w-full rounded-lg px-2.5 h-9 text-neutral-400 hover:bg-red-50 hover:text-red-500 transition-colors"
        >
          <LogOut size={16} strokeWidth={2} className="flex-shrink-0" />
          {(!collapsed || mobile) && <span className="text-[13px] font-medium">Logout</span>}
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar */}
      {!isMobile && (
        <aside
          className="relative flex flex-col flex-shrink-0 transition-all duration-200 bg-white border-r border-neutral-100"
          style={{ width: collapsed ? "68px" : "200px", minHeight: "100vh" }}
        >
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="absolute -right-3 top-[38px] z-20 w-6 h-6 rounded-full bg-white border border-neutral-200 flex items-center justify-center shadow-sm hover:bg-neutral-50 transition-colors"
          >
            {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
          </button>
          <SidebarContent mobile={false} />
        </aside>
      )}

      {/* Mobile */}
      {isMobile && (
        <>
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="fixed z-50 flex items-center justify-center w-10 h-10 text-black left-2 top-2"
          >
            <Menu size={20} />
          </button>
          {mobileMenuOpen && (
            <>
              <div className="fixed inset-0 z-40 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
              <aside className="fixed top-0 left-0 z-50 flex flex-col w-64 h-full bg-white shadow-xl">
                <div className="flex justify-end p-4 border-b border-neutral-100">
                  <button onClick={() => setMobileMenuOpen(false)} className="p-1 rounded-lg hover:bg-neutral-100">
                    <X size={18} className="text-neutral-500" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <SidebarContent mobile={true} />
                </div>
              </aside>
            </>
          )}
        </>
      )}
    </>
  );
}